import { RestartProcessingOutcome } from '@cadstrom/cadstrom-shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Command } from 'cmdk';

import { CommandBarTypedItem } from '#components/command-bar/components/command-bar-typed-item.js';
import { Toast } from '#components/toast/toast.js';
import { canRestartProcessing } from '#src/routes/projects/project-overview/restart-processing-eligibility.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';

import type { Revision } from '@cadstrom/cadstrom-shared';

export interface RestartProcessingCommandItemProps {
	revision: Revision;
	/** How the revision is labelled in the rest of the group. */
	revisionName: string;
	onClose: () => void;
}

/**
 * Command-bar action that hands a stuck revision back to design processing. Renders
 * nothing unless the viewer is staff and the revision is one a restart can rescue,
 * so the command never appears where the server would refuse it.
 *
 * @param props.revision - The revision the command would restart.
 * @param props.revisionName - How the revision is labelled in the rest of the group.
 * @param props.onClose - Dismisses the command bar once the restart is dispatched.
 */
export function RestartProcessingCommandItem({ revision, revisionName, onClose }: RestartProcessingCommandItemProps) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const addToast = useAddToast();

	// Gate on a live successful fetch, not just `data`: React Query keeps the last
	// successful `currentUser` after a later refetch 401s, which would otherwise leak
	// the staff-only command to a lapsed session.
	const { data: currentUser, isSuccess } = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, { throwOnError: false }),
	);

	const { mutate: restartProcessing } = useMutation(
		trpc.revision.restartProcessing.mutationOptions({
			onSuccess: async (data, variables) => {
				// The command bar closes over whatever list is behind it, which has no refetch
				// interval; without this the caller still sees the pre-restart status and is
				// invited to restart again.
				await Promise.all([
					queryClient.invalidateQueries({ queryKey: trpc.revision.list.queryKey() }),
					queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() }),
					queryClient.invalidateQueries({ queryKey: trpc.revision.get.queryKey({ id: variables.id }) }),
				]);
				addToast(
					<Toast
						severity='success'
						title={
							data.outcome === RestartProcessingOutcome.Started ?
								'Processing restarted'
							:	'Processing is already running'
						}
					/>,
				);
			},
			onError: (error: { message: string }) => {
				addToast(
					<Toast severity='error' title='Restart failed'>
						{error.message}
					</Toast>,
				);
			},
		}),
	);

	if (!isSuccess || !currentUser?.auth.isStaff || !canRestartProcessing(revision)) {
		return null;
	}

	return (
		<Command.Item
			value={`${revision.id} restart processing`}
			keywords={[revisionName, 'restart', 'processing', 'reprocess', 'stuck', 'workflow']}
			onSelect={() => {
				restartProcessing({ id: revision.id });
				onClose();
			}}
		>
			<CommandBarTypedItem type='revision' name='Restart processing'>
				<span className='truncate font-normal text-gray-a11'>{revisionName}</span>
			</CommandBarTypedItem>
		</Command.Item>
	);
}
